import {
  LazyHereMap,
  MapLegend,
  MapLine,
  MapMarker,
  ResetPositionButton,
  ZoomButtons,
} from '@backoffice-frontend/here-maps'
import {
  DirectionalMapMarkerIcon,
  SuspenseLoadingWrapper,
} from '@backoffice-frontend/patterns'
import type {
  AdVehicleStreamAlertVehicleMapLocationFragment,
  AdVehicleStreamAlertVehicleMapVehicleFragment,
  AdVehicleStreamAlertVehicleMapWaypointFragment,
} from './AlertVehicleMap.hook'
import { BlackDot } from './BlackDot'
import { ETALabel } from './ETALabel'
import { GreyDot } from './GreyDot'

type AlertVehicleMapProps = {
  vehicle: AdVehicleStreamAlertVehicleMapVehicleFragment
  highlightedWaypoint?: AdVehicleStreamAlertVehicleMapWaypointFragment
}

export const AlertVehicleMap = ({
  vehicle,
  highlightedWaypoint,
}: AlertVehicleMapProps) => {
  const upcomingWaypoint = vehicle.fccUpcomingWaypoints?.[0]
  const destinationEta = upcomingWaypoint?.actions[0].estimatedAt

  const latestLocation = vehicle.latestLocation?.location
  const destinationLocation = upcomingWaypoint?.stop?.routingLocation
  const highlightedLocation = highlightedWaypoint?.stop?.routingLocation

  const locationsInView = [
    latestLocation,
    destinationLocation,
    highlightedLocation,
  ].filter(
    (item): item is AdVehicleStreamAlertVehicleMapLocationFragment =>
      item?.__typename === 'Location',
  )

  if (!latestLocation) {
    return null
  }

  return (
    <SuspenseLoadingWrapper>
      <LazyHereMap
        inView={locationsInView}
        center={latestLocation}
        maxZoom={18}
        css={{ borderRadius: 8, height: '100%' }}
      >
        <MapMarker position={latestLocation}>
          <DirectionalMapMarkerIcon
            heading={vehicle.latestLocation?.heading ?? undefined}
            inService={vehicle.inService ?? undefined}
            css={{
              height: 40,
              width: 40,
            }}
          />
        </MapMarker>

        {destinationLocation && (
          <>
            <MapLine from={latestLocation} to={destinationLocation} />

            <MapMarker position={destinationLocation} css={{ marginTop: -28 }}>
              <ETALabel arrivalTime={new Date(destinationEta ?? '')} />
            </MapMarker>
          </>
        )}

        {highlightedLocation && (
          <MapMarker position={highlightedLocation}>
            <GreyDot />
          </MapMarker>
        )}

        {destinationLocation && (
          <MapMarker position={destinationLocation}>
            <BlackDot />
          </MapMarker>
        )}

        <MapLegend position="bottom-right">
          <ResetPositionButton />
          <ZoomButtons />
        </MapLegend>
      </LazyHereMap>
    </SuspenseLoadingWrapper>
  )
}
